import {
  AccountCircle,
  Business,
  Close,
  Email,
  Schedule,
  TrendingUp,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { hostname, addImageDimensions } from "../../utils";
import MetricCard from "../sequences/SequenceMetricsCard";

interface WhitelabelCustomer {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  created_date?: string;
  last_login?: string;
  companies: Array<{
    id: string;
    name: string;
    role: string;
  }>;
  usage: {
    total_credits: number;
    total_tokens: number;
    total_requests: number;
    recent_credits: number;
    recent_requests: number;
  };
  subscription: {
    plan?: string;
    whitelabel: boolean;
    whitelabelLimit: number;
  };
}

interface CustomerDetails {
  customer: {
    id: string;
    email: string;
    displayName?: string;
    photoURL?: string;
    created_date?: string;
    last_login?: string;
    referral_source?: string;
    subscription: {
      plan?: string;
      whitelabel: boolean;
      whitelabelLimit: number;
    };
    in_organization?: boolean;
  };
  companies: Array<{
    id: string;
    name: string;
    description?: string;
    industry?: string;
    size?: string;
    location?: string;
    role: string;
    joined_date?: string;
    created_date?: string;
  }>;
  usage: {
    total: {
      credits: number;
      tokens: number;
      requests: number;
      first_activity?: string;
      last_activity?: string;
    };
    by_model: Array<{
      model: string;
      credits: number;
      requests: number;
    }>;
    recent_history: Array<{
      date: string;
      credits: number;
      tokens: number;
      model: string;
      company_id?: string;
    }>;
  };
}

export default function WhitelabelCustomerDetailDialog({
  open,
  onClose,
  customer,
  whitelabelId,
  getToken,
}: {
  open: boolean;
  onClose: () => void;
  customer: WhitelabelCustomer;
  whitelabelId: string;
  getToken: Function;
}) {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<CustomerDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadCustomerDetails = async () => {
    if (!customer?.id) {
      console.log("WhitelabelCustomerDetailDialog: No customer ID provided");
      return;
    }

    console.log("WhitelabelCustomerDetailDialog: Loading details for customer", customer.id);
    setLoading(true);
    setError(null);
    const currentToken = await getToken();

    try {
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/customers/${customer.id}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      console.log("WhitelabelCustomerDetailDialog: Received response", response.data);
      setDetails(response.data);
    } catch (err) {
      console.error("WhitelabelCustomerDetailDialog: Error loading customer details:", err);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 403) {
          setError("Access denied - you are not authorized to view this customer");
        } else if (err.response?.status === 404) {
          setError("Customer not found");
        } else {
          setError(err.response?.data?.error || "Failed to load customer details");
        }
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && customer?.id) {
      loadCustomerDetails();
    }
  }, [open, customer?.id]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setDetails(null);
      setError(null);
    }
  }, [open]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format plan name for display
  const formatPlanName = (plan?: string): string => {
    if (!plan) return "Free";

    const planLower = plan.toLowerCase();
    switch (planLower) {
      case "free":
        return "Free";
      case "entrepreneur":
        return "Entrepreneur";
      case "business":
        return "Business";
      case "enterprise":
        return "Enterprise";
      default:
        // For custom/whitelabel plans, format the plan name nicely
        return plan
          .split(/[-_]/)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar src={addImageDimensions(customer.photoURL, 80, 80)} sx={{ width: 40, height: 40 }}>
              <AccountCircle />
            </Avatar>
            <Box>
              <Typography variant="h6">
                {customer.displayName || customer.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customer Details
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="error" gutterBottom>
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && !details && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              No customer details available
            </Typography>
          </Box>
        )}

        {details && (
          <Box>
            {/* Customer Info Section */}
            <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Email />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Email" 
                        secondary={details.customer.email} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Schedule />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Joined" 
                        secondary={details.customer.created_date ? moment(details.customer.created_date).format("MMMM Do, YYYY") : "-"} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TrendingUp />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Last Login" 
                        secondary={details.customer.last_login ? moment(details.customer.last_login).fromNow() : "Never"} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Subscription Plan"
                        secondary={
                          details.customer.in_organization ? (
                            <Chip
                              label="Org"
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          ) : (
                            <Chip
                              label={formatPlanName(details.customer.subscription.plan)}
                              size="small"
                              color={details.customer.subscription.plan && details.customer.subscription.plan.toLowerCase() !== "free" ? "primary" : "default"}
                            />
                          )
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Referral Source" 
                        secondary={details.customer.referral_source || "Direct"} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Account Activity" 
                        secondary={
                          details.usage.total.first_activity && details.usage.total.last_activity
                            ? `Active from ${moment(details.usage.total.first_activity).format("MMM YYYY")} to ${moment(details.usage.total.last_activity).format("MMM YYYY")}`
                            : "No activity recorded"
                        } 
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Paper>

            {/* Usage Metrics */}
            <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
              <Typography variant="h6" gutterBottom>
                Usage Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    icon={<TrendingUp color="primary" />}
                    title="Total Credits"
                    value={details.usage.total.credits.toLocaleString()}
                    color="primary.main"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    icon={<Business color="info" />}
                    title="Total Requests"
                    value={details.usage.total.requests.toLocaleString()}
                    color="info.main"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    icon={<Schedule color="success" />}
                    title="Total Tokens"
                    value={details.usage.total.tokens.toLocaleString()}
                    color="success.main"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    icon={<AccountCircle color="warning" />}
                    title="Companies"
                    value={details.companies.length.toString()}
                    color="warning.main"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Model Usage Chart */}
            {details.usage.by_model.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
                <Typography variant="h6" gutterBottom>
                  Usage by Model
                </Typography>
                <Box sx={{ height: 300 }}>
                  <PieChart
                    series={[
                      {
                        data: details.usage.by_model.map((model, index) => ({
                          id: index,
                          value: model.credits,
                          label: model.model,
                        })),
                      },
                    ]}
                    height={300}
                  />
                </Box>
              </Paper>
            )}

            {/* Companies */}
            {details.companies.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
                <Typography variant="h6" gutterBottom>
                  Companies
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Company</TableCell>
                        <TableCell>Industry</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Joined</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.companies.map((company) => (
                        <TableRow key={company.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {company.name}
                              </Typography>
                              {company.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {company.description}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{company.industry || "-"}</TableCell>
                          <TableCell>{company.size || "-"}</TableCell>
                          <TableCell>
                            <Chip 
                              label={company.role} 
                              size="small" 
                              color={company.role === "Owner" ? "primary" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            {company.joined_date 
                              ? moment(company.joined_date).format("MMM Do, YYYY")
                              : "-"
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Recent Activity */}
            {details.usage.recent_history.length > 0 && (
              <Paper sx={{ p: 3 }} variant="outlined">
                <Typography variant="h6" gutterBottom>
                  Recent Activity (Last 90 Days)
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Model</TableCell>
                        <TableCell>Credits</TableCell>
                        <TableCell>Tokens</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.usage.recent_history.slice(0, 10).map((activity, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {moment(activity.date).format("MMM Do, YYYY HH:mm")}
                          </TableCell>
                          <TableCell>
                            <Chip label={activity.model} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{activity.credits.toLocaleString()}</TableCell>
                          <TableCell>{activity.tokens.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {details.usage.recent_history.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Showing 10 of {details.usage.recent_history.length} recent activities
                  </Typography>
                )}
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}